import { Link } from "react-router-dom";

type FeatureCard = {
  icon: string;
  title: string;
  desc: string;
  bgAccent: string;
  image: string;
  link: string;
  isExternal?: boolean;
};

const features: FeatureCard[] = [
  {
    icon: "ri-star-line",
    title: "BaseTao Review",
    desc: "Read our detailed, unbiased analysis of BaseTao's platform — covering user experience, interface design, customer support quality, and real shopper feedback.",
    bgAccent: "bg-accent-50",
    image: "https://readdy.ai/api/search-image?query=Clean%20minimal%20desk%20setup%20with%20a%20laptop%20showing%20five%20star%20review%20ratings%2C%20warm%20beige%20and%20cream%20tones%2C%20soft%20natural%20lighting%2C%20editorial%20product%20photography%20style%2C%20professional%20and%20trustworthy%20atmosphere%2C%20elegant%20composition&width=400&height=200&seq=feat-review-v2&orientation=landscape",
    link: "/article/basetao-review",
  },
  {
    icon: "ri-global-line",
    title: "BaseTao Shipping Cost",
    desc: "Compare every available shipping method with estimated costs for popular destinations. Our shipping calculator helps you budget accurately with no surprise fees.",
    bgAccent: "bg-primary-50",
    image: "https://readdy.ai/api/search-image?query=Minimal%20workspace%20with%20cardboard%20shipping%20boxes%20in%20various%20sizes%20arranged%20on%20cream%20desk%2C%20measuring%20tape%20and%20calculator%20nearby%2C%20soft%20natural%20daylight%2C%20warm%20neutral%20palette%2C%20organized%20commercial%20photography%2C%20clean%20professional%20aesthetic&width=400&height=200&seq=feat-shipping-v2&orientation=landscape",
    link: "/article/basetao-shipping-cost",
  },
  {
    icon: "ri-building-4-line",
    title: "BaseTao Warehouse Guide",
    desc: "Master the entire warehouse workflow: from item arrival and storage duration limits to QC photo interpretation and parcel consolidation strategies.",
    bgAccent: "bg-secondary-50",
    image: "https://readdy.ai/api/search-image?query=Modern%20organized%20warehouse%20shelving%20concept%20in%20warm%20neutral%20tones%2C%20small%20labeled%20boxes%20neatly%20arranged%2C%20soft%20overhead%20lighting%2C%20clean%20minimalist%20industrial%20aesthetic%2C%20cream%20and%20beige%20palette%2C%20professional%20logistics%20photography%2C%20efficient%20organized%20atmosphere&width=400&height=200&seq=feat-warehouse-v2&orientation=landscape",
    link: "/article/basetao-warehouse-guide",
  },
  {
    icon: "ri-coupon-line",
    title: "BaseTao Coupon & Discounts",
    desc: "Stay updated with the latest verified BaseTao coupon codes, seasonal promotions, and platform discount events. Learn insider tips for stacking savings.",
    bgAccent: "bg-accent-50",
    image: "https://readdy.ai/api/search-image?query=Elegant%20savings%20concept%20with%20colorful%20coupon%20tags%20and%20a%20calculator%20on%20warm%20cream%20desk%2C%20soft%20natural%20lighting%2C%20editorial%20catalog%20style%2C%20warm%20beige%20and%20pastel%20tones%2C%20organized%20flat%20lay%20composition%2C%20commercial%20quality%20photography%2C%20inviting%20aesthetic&width=400&height=200&seq=feat-coupon-v2&orientation=landscape",
    link: "/article/basetao-coupon-discount",
  },
  {
    icon: "ri-shield-check-line",
    title: "Is BaseTao Legit?",
    desc: "A comprehensive safety and trust evaluation covering company background, payment security protocols, buyer protection policies, and verified user experiences.",
    bgAccent: "bg-primary-50",
    image: "https://readdy.ai/api/search-image?query=Modern%20security%20and%20trust%20concept%2C%20clean%20workspace%20with%20shield%20icon%20on%20laptop%20screen%20and%20verification%20badge%2C%20warm%20natural%20lighting%2C%20cream%20beige%20palette%2C%20professional%20editorial%20photography%2C%20reassuring%20trustworthy%20atmosphere%2C%20soft%20shadows%2C%20high%20quality%20commercial%20shot&width=400&height=200&seq=feat-legit-v2&orientation=landscape",
    link: "/article/is-basetao-legit",
  },
];

const FeatureCardComponent = ({ feat }: { feat: FeatureCard }) => {
  const content = (
    <div className="group flex flex-col rounded-xl border border-background-200/60 bg-background-50 overflow-hidden hover:border-primary-200/60 transition-all cursor-pointer">
      <div className="h-36 overflow-hidden">
        <img
          src={feat.image}
          alt={feat.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className={`w-9 h-9 rounded-lg ${feat.bgAccent} flex items-center justify-center mb-3`}>
          <i className={`${feat.icon} text-lg text-foreground-800`}></i>
        </div>
        <h3 className="font-heading text-base text-foreground-900 mb-2">{feat.title}</h3>
        <p className="text-sm text-foreground-600 leading-relaxed flex-1">{feat.desc}</p>
        <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 mt-3">
          Read More <i className="ri-arrow-right-line"></i>
        </span>
      </div>
    </div>
  );

  if (feat.isExternal) {
    return (
      <a href={feat.link} target="_blank" rel="noopener nofollow">
        {content}
      </a>
    );
  }

  return <Link to={feat.link}>{content}</Link>;
};

export default function FeatureCards() {
  return (
    <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-6xl mx-auto">
        <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 text-center mb-4">
          Everything You Need Before Using BaseTao
        </h2>
        <p className="text-sm text-foreground-600 text-center max-w-xl mx-auto mb-12">
          These essential resources help you navigate BaseTao with confidence — from first-time setup to advanced spreadsheet strategies.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feat) => (
            <FeatureCardComponent key={feat.title} feat={feat} />
          ))}
        </div>
      </div>
    </section>
  );
}